import { FormikHelpers } from "formik";
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { applyApiErrors } from "@/components/Formik";
import { UserContext } from "@/components/User";
import { errorEntryType, hasCode } from "@/core/api";
import { addCurrentUserCompany, ICompany, ICompanyDto, serializeCompanyDto } from "@/data/companies";
import { getConsultantProfileCompany } from "@/data/consultant-profiles";
import { ISessionUser, refresh } from "@/data/session";
import { MessagesContext } from "@/eworkitComponents";
import { SessionContext } from "@/infrastructure/Session";
import { useToasts } from "@/views/components/Toast/useToasts";
import { router } from "@/views/router";

import { FORM_MESSANGER_CONTEXT, ICreateCompanyFormValues } from "../Create/CreateCompanyForm";
import { useHasUserCompanyWithVat } from "./useHasUserCompanyWithVat";

export type CompanySubmitReturn =
  | { result: "ask-to-join" }
  | { result: "success"; profileId: number }
  | { result: "error" };

export const useCreateCompanySubmit = () => {
  const Messages = useContext(MessagesContext);
  const Session = useContext(SessionContext);
  const history = useHistory();
  const User = useContext(UserContext);
  const { addToast } = useToasts();

  const { t } = useTranslation(["session", "common"]);

  const hasCompanyWithVat = useHasUserCompanyWithVat();

  return useCallback(
    async (
      values: ICreateCompanyFormValues,
      actions: FormikHelpers<ICreateCompanyFormValues>,
    ): Promise<CompanySubmitReturn> => {
      // eslint-disable-next-line no-async-promise-executor
      return new Promise(async resolve => {
        const resolvePromise = (result: CompanySubmitReturn) => {
          actions.setSubmitting(false);
          resolve(result);
        };

        const handleSuccess = async (user: ISessionUser, newCompany: ICompany) => {
          const newConsultantProfile = user.consultantProfiles.find(p => {
            const profileCompany = getConsultantProfileCompany(p);
            return profileCompany !== undefined && profileCompany.id === newCompany.id;
          });
          await User.setConsultantProfileContext(newConsultantProfile!.id);
          history.push(router.get("dashboard").path);
          resolvePromise({ result: "success", profileId: newConsultantProfile!.id });
        };

        try {
          const newCompanyDto = serializeCompanyDto(values as ICompanyDto);
          const newCompany = await addCurrentUserCompany({}, newCompanyDto);
          const sessionUser = await refresh();

          addToast({
            text: t("COMPANY.CREATE.SUCCESS_MESSAGES.COMPANY_CREATED"),
            type: "success",
          });

          Session.update(sessionUser, () => {
            handleSuccess(sessionUser, newCompany);
          });
        } catch (response) {
          const payload = errorEntryType(response.payload);
          const pendingInvitationCode = hasCode(payload, "PENDING_INVITATION");

          const vatError = payload.errors.find(e => e.field === "vatNo");
          const askToJoin =
            vatError && vatError.code === "NOT_UNIQUE" && !hasCompanyWithVat(values.vatNo, values.countryCode);
          if (pendingInvitationCode) {
            Messages.addMessage({
              text: t("COMPANY.CREATE.ERROR_MESSAGES.PENDING_INVITATION"),
              type: "warning",
              context: FORM_MESSANGER_CONTEXT,
            });
          } else if (!askToJoin) {
            Messages.addMessage({
              text: t("common:VALIDATION_MESSAGES.GENERAL"),
              type: "warning",
              context: FORM_MESSANGER_CONTEXT,
            });
            applyApiErrors(actions, response.payload);
          } else {
            resolvePromise({ result: "ask-to-join" });
          }

          resolvePromise({ result: "error" });
        }
      });
    },
    [User, history, Messages, t, Session, hasCompanyWithVat],
  );
};
